import { useState, useCallback, useEffect } from 'react';
import { storage } from '../utils/localStorage';

export interface ScreenNotification {
  id: string;
  screenId: string;
  action: 'created' | 'updated' | 'deleted';
  recordId: number;
  recordText: string;
  user?: string;
  timestamp: number;
  read: boolean;
}

const MAX_NOTIFICATIONS = 20;
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

export const useScreenNotifications = (screenId: string) => {
  const storageKey = `notifications_${screenId}`;

  const loadNotifications = useCallback((): ScreenNotification[] => {
    const stored = storage.getItem(storageKey);
    if (!stored) return [];

    try {
      const notifications: ScreenNotification[] = JSON.parse(stored);
      const now = Date.now();

      // Filter out old notifications (older than 24h)
      return notifications.filter(n => (now - n.timestamp) < MAX_AGE_MS);
    } catch {
      return [];
    }
  }, [storageKey]);

  const [notifications, setNotifications] = useState<ScreenNotification[]>(loadNotifications);

  const saveNotifications = useCallback((notifs: ScreenNotification[]) => {
    storage.setItem(storageKey, JSON.stringify(notifs));
    setNotifications(notifs);
  }, [storageKey]);

  const addNotification = useCallback((
    action: 'created' | 'updated' | 'deleted',
    recordId: number,
    recordText: string,
    user?: string
  ) => {
    const notification: ScreenNotification = {
      id: `${screenId}_${recordId}_${Date.now()}`,
      screenId,
      action,
      recordId,
      recordText,
      user,
      timestamp: Date.now(),
      read: false,
    };

    const updated = [notification, ...notifications].slice(0, MAX_NOTIFICATIONS);
    saveNotifications(updated);
  }, [screenId, notifications, saveNotifications]);

  const markAsRead = useCallback((notificationId: string) => {
    const updated = notifications.map(n =>
      n.id === notificationId ? { ...n, read: true } : n
    );
    saveNotifications(updated);
  }, [notifications, saveNotifications]);

  const markAllAsRead = useCallback(() => {
    const updated = notifications.map(n => ({ ...n, read: true }));
    saveNotifications(updated);
  }, [notifications, saveNotifications]);

  const dismissNotification = useCallback((notificationId: string) => {
    const updated = notifications.filter(n => n.id !== notificationId);
    saveNotifications(updated);
  }, [notifications, saveNotifications]);

  const clearAll = useCallback(() => {
    saveNotifications([]);
  }, [saveNotifications]);

  const unreadCount = notifications.filter(n => !n.read).length;

  // Clean up old notifications on mount
  useEffect(() => {
    const cleaned = loadNotifications();
    if (cleaned.length !== notifications.length) {
      saveNotifications(cleaned);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    clearAll,
  };
};
